import { memo, useRef } from 'react';
import { Empty } from 'antd';
import { ChatMessage } from 'modules/chatbot/hooks';
import { MessageStatus } from 'modules/chatbot/types/chatbot.types';
import ChatBubble from 'modules/chatbot/layouts/ChatBubbleLayout/ChatBubbleLayout';
import styles from './messageList.module.less';

interface MessageListProps {
  messages: ChatMessage[];
  className?: string;
  maxHeight?: number;
  virtualized?: boolean;
}

const MessageList = memo<MessageListProps>(
  ({
    messages,
    className,
    maxHeight,
    virtualized = false,
  }: MessageListProps) => {
    const listRef = useRef<HTMLDivElement>(null);

    const listClasses = [
      styles.messageList,
      virtualized && styles.virtualized,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const listStyle = maxHeight ? { maxHeight } : undefined;

    // Empty state
    if (messages.length === 0) {
      return (
        <div className={listClasses} style={listStyle}>
          <Empty
            description="No messages yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            className={styles.emptyState}
          />
        </div>
      );
    }

    return (
      <div
        ref={listRef}
        className={listClasses}
        style={listStyle}
        role="log"
        aria-live="polite"
        aria-atomic="false"
        aria-label={`Chat messages, ${messages.length} total`}
        tabIndex={0}
      >
        <div className={styles.messagesContainer}>
          {messages.map((message: ChatMessage) => (
            <ChatBubble
              key={message.id}
              messageId={message.id}
              content={message.content}
              sender={message.sender}
              isPending={message.status === MessageStatus.PENDING}
              status={message.status}
              eventType={message.metadata?.eventType}
              sources={message.sources}
            />
          ))}
        </div>
      </div>
    );
  }
);

MessageList.displayName = 'MessageList';

export default MessageList;
